import { useCallback, useMemo } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";

import { FETCH_PHOTO_MATERIAL_V2 } from "../graphql/query/PhotoMaterial";

const mapPhotoMaterials = edges =>
  edges.map(photoMaterial => ({
    id: photoMaterial.id,
    artworkId: photoMaterial.artworkId,
    number: photoMaterial.number,
    credit: photoMaterial.credit,
    description: photoMaterial.description,
    photographer: photoMaterial.photographer,
    format: photoMaterial.format,
  }));

const usePhotoMaterialResult = data => {
  const edges = useMemo(() => data?.photoMaterialsV2?.edges || [], [data]);

  const findPhotoMaterialById = useCallback(
    id => edges.find(photoMaterial => photoMaterial.id === id),
    [edges],
  );

  const findPhotoMaterialsByArtworkId = useCallback(
    artworkId =>
      edges.filter(photoMaterial => photoMaterial.artworkId === artworkId),
    [edges],
  );

  const photoMaterials = useMemo(() => mapPhotoMaterials(edges), [edges]);

  return {
    count: data?.photoMaterialsV2?.count || 0,
    photoMaterials,
    findPhotoMaterialById,
    findPhotoMaterialsByArtworkId,
  };
};

export const usePhotoMaterial = (input, fragmentProps = {}) => {
  const {
    data = {},
    loading,
    error,
  } = useQuery(FETCH_PHOTO_MATERIAL_V2(fragmentProps), {
    variables: { input },
    skip: !input,
  });

  return { error, loading, ...usePhotoMaterialResult(data) };
};

export const usePhotoMaterialLazy = (fragmentProps = {}, options = {}) => {
  const [fetchPhotoMaterials, { data = {}, loading, error }] = useLazyQuery(
    FETCH_PHOTO_MATERIAL_V2(fragmentProps),
    options,
  );

  const fetch = useCallback(
    input => fetchPhotoMaterials({ variables: { input } }),
    [fetchPhotoMaterials],
  );

  return [fetch, { error, loading, ...usePhotoMaterialResult(data) }];
};
